<script lang="ts">
import { defineComponent, PropType, ref, watch } from 'vue';
import { DropdownPlacement } from '@/enums/DropdownPlacement.enum';
import { IS_ENV_WEBCOMPONENT } from '@/utils/constants.utils';

enum ComponentEvent {
	CLOSE = 'close',
	CHANGE_VISIBILITY = 'changeVisibility',
}

/**
 *
 * @version 1.0.0
 * @since
 */
export default defineComponent({
	name: 'CDropdown',
	props: {
		toShow: {
			required: false,
			type: Boolean,
			default: false,
		},
		placement: {
			required: false,
			type: String as PropType<DropdownPlacement>,
			default: () => DropdownPlacement.BOTTOM,
		},
		hideAnimation: {
			required: false,
			type: Boolean,
			default: false,
		},
		shadowRoot: {
			required: true,
			type: [ShadowRoot, null],
		},
	},
	setup(props) {
		const visibility = ref(props.toShow);

		const toShowSource = () => props.toShow;
		const updateVisibility = (toShow: boolean) => {
			visibility.value = toShow;
		};
		watch(toShowSource, updateVisibility);

		return {
			visibility,
		};
	},
	data() {
		return {
			styleContent: {},
			styleContainer: {},
		};
	},
	watch: {
		visibility: {
			immediate: true,
			handler(toShowNewVal) {
				const listener = this.closeDropdown as EventListener;
				if (toShowNewVal) {
					if (IS_ENV_WEBCOMPONENT && this.shadowRoot) {
						this.shadowRoot.addEventListener('click', listener);
          } else {
						document.addEventListener('click', listener);
					}
				} else {
					if (IS_ENV_WEBCOMPONENT && this.shadowRoot) {
						this.shadowRoot.removeEventListener('click', listener);
          } else {
						document.removeEventListener('click', listener);
					}
				}
			},
		},
	},
	beforeUnmount() {
		if (IS_ENV_WEBCOMPONENT && this.shadowRoot) {
			this.shadowRoot.removeEventListener('click', this.closeDropdown as EventListener);
    } else {
			document.removeEventListener('click', this.closeDropdown as EventListener);
		}
	},
	mounted() {
		const btnEle = this.$refs.trigger as HTMLElement;
		const height = btnEle?.offsetHeight;

		switch (this.placement) {
			case DropdownPlacement.LEFT: {
				this.styleContent = {
					top: `-${height}px`,
					right: '100%',
					'margin-right': '8px',
				};
				break;
			}
			case DropdownPlacement.RIGHT: {
				this.styleContent = {
					top: `-${height}px`,
					left: '100%',
					'margin-left': '8px',
				};
				break;
			}
			// Bottom
			default: {
				this.styleContent = {
					'margin-top': '8px',
					right: 0,
				};
				break;
			}
		}
	},
	methods: {
		toggleVisibility(): void {
			this.visibility = !this.visibility;
			this.$emit(ComponentEvent.CHANGE_VISIBILITY, this.visibility);
		},
		closeDropdown(event: MouseEvent): void {
			const dropdownEle = this.$refs.dropdown as HTMLElement;
			if (dropdownEle?.contains((event as any).target)) {
				return;
			}
			this.visibility = false;
			this.$emit(ComponentEvent.CLOSE);
			this.$emit(ComponentEvent.CHANGE_VISIBILITY, this.visibility);
		},
	},
});
</script>

<template>
	<div
		ref="dropdown"
		class="c-dropdown__container"
		:style="styleContainer"
	>
		<div
			ref="trigger"
			class="c-dropdown__trigger"
		>
			<slot
				name="trigger"
				:toggle-visibility="toggleVisibility"
			/>
		</div>
		<transition :name="String(hideAnimation) || 'fade'">
			<div
				v-show="visibility"
				class="c-dropdown"
			>
				<div
					class="c-dropdown__content"
					:style="styleContent"
				>
					<slot
						name="content"
						:toggle-visibility="toggleVisibility"
					/>
				</div>
			</div>
		</transition>
	</div>
</template>

<style scoped lang="scss">
.c-dropdown__container {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
}

.c-dropdown__trigger {
	cursor: pointer;
	padding: 0;
	border: none;
}

.c-dropdown {
	position: relative;
	display: block;
	width: 100%;
}

.c-dropdown__content {
	position: absolute;
	z-index: 10;
	border-radius: 4px;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
	background: tokens.$color-white;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
